import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import Page from '../Page';
import AuthModal from '../Shared/AuthModal';
import Footer from '../Shared/NewFooter';
import Header from '../Shared/NewHeader';
import { PageWrapper } from '../styles';

import { CURRENT_HOST_REQUIREMENTS } from '../../content/hostRequirements';
import copy from '../../content/list-your-venue';
import { getShowForgot, getShowSignIn, getShowSignUp, getUser, setShowSignIn, setShowSignUp } from '../../store/user';
import { tagRequirementsNo, tagRequirementsView, tagRequirementsYes } from '../../tag-manager/listYourVenue';
import { saveConfirmation } from './confirmation';
import {
  AccountBox,
  Body,
  ButtonRow,
  Choice,
  Choices,
  Hint,
  Intro,
  PrimaryButton,
  SecondaryButton,
  Small,
  Statement,
  Statements,
  Wrapper
} from './styles';

// Where a host goes after every statement is "Yes". Fixed in code: the page never takes
// a destination from the URL. `new=1` opens the "add a venue" form.
export const EDITOR_PATH = '/host/venues/?new=1';
export const NOT_A_FIT_PATH = '/list-your-venue/not-a-fit';

const words = copy.requirements;

const Requirements = () => {
  const { version, statements } = CURRENT_HOST_REQUIREMENTS;
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector(getUser);
  const signedIn = !!(user && user.id);
  const showSignUp = useSelector(getShowSignUp);
  const showSignIn = useSelector(getShowSignIn);
  const showForgot = useSelector(getShowForgot);
  const authOpen = showSignUp || showSignIn || showForgot;

  // Answers start blank each time the page opens: blank is not "No".
  const [answers, setAnswers] = useState({});
  const [needsAccount, setNeedsAccount] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    tagRequirementsView(version);
  }, [version]);

  // After sign-up or sign-in, go on to the editor. Waits for the user record,
  // with no timer, so a slow sign-in never sends the host away.
  useEffect(() => {
    if (needsAccount && signedIn) {
      history.push(EDITOR_PATH);
    }
  }, [needsAccount, signedIn, history]);

  const allAnswered = statements.every(({ id }) => answers[id]);
  const noIds = statements.filter(({ id }) => answers[id] === 'no').map(({ id }) => id);

  const onContinue = () => {
    if (!allAnswered) return;
    if (noIds.length) {
      tagRequirementsNo(version, noIds);
      history.push(NOT_A_FIT_PATH, { version, noIds });
      return;
    }
    tagRequirementsYes(version);
    saveConfirmation(version);
    if (signedIn) {
      history.push(EDITOR_PATH);
    } else {
      setNeedsAccount(true);
      dispatch(setShowSignUp());
    }
  };

  return (
    <PageWrapper>
      <Header omitSearch />
      <Wrapper>
        <Page title={words.title}>
          <Body>
            <Intro>{words.intro}</Intro>
            <Statements>
              {statements.map(({ id, text }, index) => (
                <Statement key={id}>
                  <fieldset>
                    <legend>
                      {index + 1}. {text}
                    </legend>
                    <Choices>
                      {['yes', 'no'].map((value) => (
                        <Choice key={value}>
                          <input
                            type="radio"
                            name={`requirement-${id}`}
                            value={value}
                            checked={answers[id] === value}
                            onChange={() => {
                              setNeedsAccount(false);
                              setAnswers((prev) => ({ ...prev, [id]: value }));
                            }}
                          />
                          {words[value]}
                        </Choice>
                      ))}
                    </Choices>
                  </fieldset>
                </Statement>
              ))}
            </Statements>

            <PrimaryButton type="button" onClick={onContinue} disabled={!allAnswered} aria-describedby={allAnswered ? undefined : 'requirements-hint'}>
              {words.continue}
            </PrimaryButton>
            {!allAnswered && <Hint id="requirements-hint">{words.answerAll}</Hint>}

            {needsAccount && !signedIn && !authOpen && (
              <AccountBox>
                <p>{words.account}</p>
                <ButtonRow>
                  <PrimaryButton type="button" onClick={() => dispatch(setShowSignUp())}>
                    {words.createAccount}
                  </PrimaryButton>
                  <SecondaryButton type="button" onClick={() => dispatch(setShowSignIn())}>
                    {words.signIn}
                  </SecondaryButton>
                </ButtonRow>
              </AccountBox>
            )}

            <Small>
              {words.questions} <a href={`mailto:${copy.supportEmail}`}>{copy.supportEmail}</a>.
            </Small>
          </Body>
        </Page>
      </Wrapper>
      <Footer />
      <AuthModal />
    </PageWrapper>
  );
};

export default Requirements;
